import { useMemo } from 'react';
import SchemaSipProfileService from '@frontend/api-sip-profile';
import { SoftphoneQueries } from '@frontend/api-softphone';
import { http } from '@frontend/fetch';
import { useQuery } from '@frontend/react-query-helpers';

export const useSoftphoneSettingsQuery = ({
  userAgent,
  tenantId,
  sipProfileId,
  enabled,
}: {
  userAgent: string;
  tenantId: string;
  sipProfileId: string;
  enabled: boolean;
}) => {
  const {
    data: settings,
    refetch: refetchSettings,
    isFetching,
  } = SoftphoneQueries.useGetCurrentUserSoftphoneQuery({
    req: { userAgent },
    opts: {
      enabled,
      keepPreviousData: true,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      tenantId,
    },
  });

  const { softphone, sipProfile } = useMemo(() => {
    const softphone = settings?.softphones.find((softphone) => {
      return softphone.sipProfiles.some((sipProfile) => {
        return sipProfile.id === sipProfileId;
      });
    });
    return {
      softphone,
      sipProfile: softphone?.sipProfiles.find((sipProfile) => sipProfile.id === sipProfileId),
    };
  }, [settings, sipProfileId]);

  return {
    settings,
    softphone,
    sipProfile,
    refetchSettings,
    isFetching,
  };
};

export const useSoftphoneRegistrationQuery = ({
  tenantId,
  sipProfileId,
  enabled,
  lastRegisteredCallId,
}: {
  tenantId: string;
  sipProfileId: string;
  enabled: boolean;
  lastRegisteredCallId: string;
}) => {
  const { data, error, refetch, isRefetching, isFetching } = useQuery({
    queryKey: [tenantId, sipProfileId, 'softphone-registration'],
    queryFn: () =>
      SchemaSipProfileService.GetRegistration({
        sipProfileId,
      }),
    enabled,
    refetchOnMount: 'always',
    refetchOnReconnect: 'always',
    staleTime: 0,
  });

  const isRegistrationAvailable = data && !data.registration.active;
  const isRegistrationActive = data?.registration.active && lastRegisteredCallId === data.registration.callId;
  const firstTimeRegistering = http.isHttpError(error) && error.status === 404;
  const isRegistrationAllowed = isRegistrationAvailable || firstTimeRegistering;

  return {
    isRegistrationStatusFetching: isRefetching || isFetching,
    isRegistrationAllowed,
    isRegistrationActive,
    refetchRegistrationStatus: refetch,
    firstTimeRegistering,
    registration: data?.registration,
  };
};
